import type { ReactElement } from "react";
import { useId } from "react";

import { FormFieldLabel } from "@core/components/FormFieldLabel";
import { SupportingText } from "@core/components/SupportingText";
import type { SafeHtmlAttributes } from "@core/types/SafeHtmlAttributes";
import classnames from "classnames";

import type { RadioButton, RadioButtonProps } from "./components/RadioButton";

import { RadioButtonGroupContext } from "./RadioButtonGroupContext";

import styles from "./RadioButtonGroup.module.scss";

type RadioButtonChild = ReactElement<RadioButtonProps, typeof RadioButton> | false | null | undefined;

export type RadioButtonGroupProps = {
  name?: string;
  label?: string;
  helperText?: string;
  errorText?: string;
  isError?: boolean;
  orientation?: "vertical" | "horizontal";
  children: RadioButtonChild | RadioButtonChild[];
} & SafeHtmlAttributes<HTMLFieldSetElement>;

export const RadioButtonGroup = ({
  name,
  label,
  helperText,
  errorText,
  isError = false,
  orientation = "vertical",
  children,
  "aria-describedby": ariaDescribedBy,
  ...rest
}: RadioButtonGroupProps) => {
  const supportingTextId = useId();
  const labelId = useId();
  const showHelperText = !isError && helperText;
  const showErrorText = isError && errorText;
  const resolvedAriaDescribedBy =
    [ariaDescribedBy, showHelperText || showErrorText ? supportingTextId : undefined]
      .flatMap((value) => value?.split(/\s+/) ?? [])
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <fieldset
      {...rest}
      className={styles.group}
      role="radiogroup"
      aria-labelledby={label ? labelId : undefined}
      aria-describedby={resolvedAriaDescribedBy}
      aria-invalid={isError || undefined}
    >
      {label && (
        <legend id={labelId} className={styles.legend}>
          <FormFieldLabel label={label} />
        </legend>
      )}

      <RadioButtonGroupContext.Provider value={{ isError, groupName: name, orientation }}>
        <div className={classnames(styles.list, styles[orientation])}>{children}</div>
      </RadioButtonGroupContext.Provider>

      {showHelperText && (
        <SupportingText id={supportingTextId} variant="info">
          {helperText}
        </SupportingText>
      )}

      {showErrorText && (
        <SupportingText id={supportingTextId} variant="error" role="alert">
          {errorText}
        </SupportingText>
      )}
    </fieldset>
  );
};
